<template>
  <v-row>
    <v-col cols="12">
      <v-card class="tkt-card" flat>
        <!-- HEADER BAND -->
        <div class="tkt-header pl-base-bg">
          <div class="tkt-header-icon">
            <v-icon color="#fff" size="22">mdi-ticket-outline</v-icon>
          </div>
          <div>
            <div class="tkt-title">Edit Ticket</div>
            <div class="tkt-subtitle">{{ form.name || "Update support ticket" }}</div>
          </div>

          <v-spacer />

          <v-chip
            :color="statusColor(form.status)"
            dark
            small
            class="text-uppercase font-weight-bold"
          >
            {{ form.status?.replace('_', ' ') }}
          </v-chip>
        </div>

        <!-- FORM -->
        <v-form ref="form" v-model="valid" class="pa-6">
          <div class="tkt-section-label">Ticket Details</div>
          <v-row dense>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.name"
                label="Name"
                outlined
                dense
                :rules="[rules.required]"
                prepend-inner-icon="mdi-account"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model="form.phone"
                label="Phone"
                outlined
                dense
                :rules="[rules.required]"
                prepend-inner-icon="mdi-phone"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-select
                v-model="form.issueType"
                :items="issueTypes"
                label="Issue Type"
                outlined
                dense
                :rules="[rules.required]"
                prepend-inner-icon="mdi-alert-circle-outline"
              />
            </v-col>

            <v-col cols="12" sm="6">
              <v-select
                v-model="form.status"
                :items="statuses"
                label="Status"
                outlined
                dense
                :rules="[rules.required]"
                prepend-inner-icon="mdi-progress-check"
              />
            </v-col>

            <v-col cols="12">
              <v-textarea
                v-model="form.message"
                label="Message"
                outlined
                dense
                rows="3"
                :rules="[rules.required]"
                prepend-inner-icon="mdi-text-box-outline"
              />
            </v-col>
          </v-row>

          <!-- ACTIONS -->
          <v-row class="mt-4" justify="end" align="center" no-gutters>
            <v-btn color="error" outlined @click="confirmDelete = true">
              Delete
            </v-btn>

            <v-btn color="secondary" class="ml-3" @click="reset">Reset</v-btn>

            <v-btn
              class="tkt-save-btn ml-3"
              :loading="loading"
              @click="submit"
            >
              Update Ticket
            </v-btn>
          </v-row>
        </v-form>
      </v-card>
    </v-col>

    <!-- DELETE DIALOG -->
    <global-dialog
      v-if="confirmDelete"
      v-model="confirmDelete"
      :buttons="buttons"
      title="Confirm Delete"
    >
      <p>Are you sure you want to delete this ticket?</p>
    </global-dialog>
  </v-row>
</template>

<script>
import GlobalDialog from "./common/global-dialog.vue";

export default {
  name: "EditTicket",
  components: { GlobalDialog },

  data() {
    return {
      valid: false,
      loading: false,
      confirmDelete: false,

      form: {
        name: "",
        phone: "",
        issueType: "",
        message: "",
        status: "open",
      },

      issueTypes: [
        "Technical Issue",
        "Billing Issue",
        "Feature Request",
        "Bug Report",
      ],

      statuses: ["open", "in_progress", "resolved"],

      rules: {
        required: (v) => !!v || "Required",
      },
    };
  },

  async created() {
    const id = this.$route.params.id;
    await this.loadTicket(id);
  },

  computed: {
    buttons() {
      return [
        {
          text: "Cancel",
          action: () => (this.confirmDelete = false),
          outlined: true,
          color: "#0a5a90",
        },
        {
          text: "Delete",
          action: this.deleteTicket,
          color: "error",
        },
      ];
    },
  },

  methods: {
    async loadTicket(id) {
      try {
        const ticket = await this.$store.dispatch("ticket/getTicket", id);

        this.form = {
          name: ticket.name,
          phone: ticket.phone,
          issueType: ticket.issueType,
          message: ticket.message,
          status: ticket.status,
        };
      } catch (err) {
        this.$toast.error("Failed to load ticket");
        this.$router.push("/tickets");
      }
    },

    async submit() {
      if (!this.$refs.form.validate()) return;

      this.loading = true;
      try {
        const id = this.$route.params.id;

        await this.$store.dispatch("ticket/updateTicket", {
          id,
          payload: this.form,
        });

        this.$toast.success("Ticket updated successfully");
        this.$router.push("/tickets");
      } catch (err) {
        this.$toast.error("Failed to update ticket");
      } finally {
        this.loading = false;
      }
    },

    async deleteTicket() {
      const id = this.$route.params.id;

      try {
        await this.$store.dispatch("ticket/deleteTicket", id);

        this.$toast.success("Ticket deleted successfully");
        this.$router.push("/tickets");
      } catch (err) {
        this.$toast.error("Failed to delete ticket");
      } finally {
        this.confirmDelete = false;
      }
    },

    reset() {
      this.$refs.form.reset();
    },

    statusColor(status) {
      if (status === "resolved") return "green";
      if (status === "in_progress") return "orange";
      return "grey";
    },
  },
};
</script>

<style scoped>
.tkt-card {
  border-radius: 0;
  overflow: hidden;
  box-shadow: 0 6px 24px rgba(15, 30, 50, 0.1);
}

.tkt-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 20px;
}

.tkt-header-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.16);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.tkt-title {
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.01em;
}

.tkt-subtitle {
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 2px;
}

.tkt-section-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #94a3b8;
  margin-bottom: 10px;
}

.tkt-save-btn {
  background: var(--pl-base-bg-gradient) !important;
  color: #fff !important;
  font-weight: 700 !important;
  text-transform: none !important;
  box-shadow: none !important;
}
</style>
